import { AddressModalProps, countries } from '../utils/types';
import React, { useCallback, useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { useTranslation } from 'react-i18next';

const AddressModal: React.FC<AddressModalProps> = ({
  isOpen,
  onClose,
  onSubmit,
  address,
  required = false,
}) => {
  const { t } = useTranslation(['translation', 'dynamic']);
  const [formData, setFormData] = useState({ ...address });

  const [submitted, setSubmitted] = useState(false);
  const modalRef = useRef<HTMLDivElement>(null);
  type RequiredField = 'country' | 'address1' | 'zip' | 'city' | 'phone';
  const missing = (name: RequiredField) =>
    required &&
    (!formData[name]?.trim() ||
      (name === 'country' && !formData.country_code?.trim()));
  const focusFirstInvalid = useCallback(() => {
    const field = modalRef.current?.querySelector<HTMLElement>(
      '[data-missing="true"]'
    );
    field?.focus();
    return !!field;
  }, []);
  useEffect(() => {
    if (isOpen) {
      setSubmitted(false);
      focusFirstInvalid();
    }
  }, [isOpen, required, focusFirstInvalid]);
  const fieldProps = (name: RequiredField) => ({
    id: `pickup-${name}`,
    required,
    'data-missing': missing(name),
    'aria-invalid': submitted && missing(name),
    'aria-describedby':
      submitted && missing(name) ? `pickup-${name}-error` : undefined,
  });
  const fieldError = (name: RequiredField) =>
    submitted && missing(name) ? (
      <div id={`pickup-${name}-error`} className="xc-field__error" role="alert">
        {t('validation.required')}
      </div>
    ) : null;

  const handleInputChange = (
    e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>
  ) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value,
      ...(name === 'country'
        ? {
            country_code:
              countries.find(country => country.name === value)?.code ?? null,
          }
        : {}),
      id: null,
    }));
  };

  const handleSubmit = () => {
    setSubmitted(true);
    if (focusFirstInvalid()) return;
    onSubmit(formData);
  };

  if (!isOpen) return null;

  return createPortal(
    <>
      {/* Bootstrap Modal Backdrop */}
      <div
        className="modal-backdrop fade show custom-modal-overlay close-modal"
        onClick={onClose}
      />

      {/* Bootstrap Modal */}
      <div
        ref={modalRef}
        className="modal fade show d-block custom-modal-container"
        tabIndex={-1}
        style={{ zIndex: 1055 }}
      >
        <div
          className="modal-dialog modal-dialog-centered"
          style={{ maxWidth: '500px' }}
          onClick={e => e.stopPropagation()}
        >
          <div className="modal-content bg-light text-dark">
            {/* Modal Header */}
            <div className="modal-header border-0 pb-2">
              <h5 className="modal-title fw-bold text-dark">
                {t('addressModal.pickupAddress')}
              </h5>
              <button
                type="button"
                className="btn-close"
                onClick={onClose}
                aria-label="Close"
              />
            </div>

            {/* Modal Body */}
            <div className="modal-body pt-0 align-content-start text-start">
              <p className="text-muted mb-4" style={{ fontSize: '14px' }}>
                {t('addressModal.setUp')}
              </p>

              {/* Address and Contact Details Section */}
              <div className="mb-4">
                <h6 className="fw-bold mb-3 text-dark">
                  {t('addressModal.details')}
                </h6>

                {/* Country */}
                <div className="mb-3">
                  <label
                    htmlFor="pickup-country"
                    className="form-label text-muted small"
                  >
                    {t('addressModal.country')}
                    <span className="text-primary">*</span>
                  </label>
                  <select
                    className="form-select"
                    name="country"
                    {...fieldProps('country')}
                    value={formData.country ?? ''}
                    onChange={handleInputChange}
                  >
                    {!countries.some(
                      country => country.name === formData.country
                    ) && (
                      <option value={formData.country ?? ''}>
                        {formData.country || '—'}
                      </option>
                    )}
                    {countries.map(country => (
                      <option key={country.code} value={country.name}>
                        {country.name}
                      </option>
                    ))}
                  </select>
                  {fieldError('country')}
                </div>

                {/* First and last name */}
                <div className="mb-3 row">
                  <div className="col-6">
                    <label className="form-label text-muted small">
                      {t('addressModal.fName')}
                      <span className="text-primary">*</span>
                    </label>
                    <input
                      type="text"
                      className="form-control"
                      name="first_name"
                      value={formData.first_name}
                      onChange={handleInputChange}
                    />
                  </div>
                  <div className="col-6">
                    <label className="form-label text-muted small">
                      {t('addressModal.lName')}
                      <span className="text-primary">*</span>
                    </label>
                    <input
                      type="text"
                      className="form-control"
                      name="last_name"
                      value={formData.last_name}
                      onChange={handleInputChange}
                    />
                  </div>
                </div>

                {/* Address */}
                <div className="mb-3">
                  <label
                    htmlFor="pickup-address1"
                    className="form-label text-muted small"
                  >
                    {t('addressModal.address')}
                    <span className="text-primary">*</span>
                  </label>
                  <input
                    type="text"
                    className="form-control"
                    autoFocus={!required}
                    name="address1"
                    {...fieldProps('address1')}
                    value={formData.address1}
                    onChange={handleInputChange}
                    placeholder={
                      t('addressModal.addPlaceholder') ?? 'Street, number, etc.'
                    }
                  />
                  {fieldError('address1')}
                </div>

                {/* Apartment */}
                <div className="mb-3">
                  <label className="form-label text-muted small">
                    {t('addressModal.add2')}
                  </label>
                  <input
                    type="text"
                    className="form-control"
                    name="address2"
                    value={formData.address2 ? formData.address2 : ''}
                    onChange={handleInputChange}
                    placeholder={
                      t('addressModal.add2Placeholder') ??
                      'Apartment, room etc.'
                    }
                  />
                </div>

                {/* Postal Code, City, County */}
                <div className="mb-3 row">
                  <div className="col-4">
                    <label
                      htmlFor="pickup-zip"
                      className="form-label text-muted small"
                    >
                      {t('addressModal.zip')}
                      {required && <span className="text-primary">*</span>}
                    </label>
                    <input
                      type="text"
                      className="form-control"
                      name="zip"
                      {...fieldProps('zip')}
                      value={formData.zip}
                      onChange={handleInputChange}
                    />
                    {fieldError('zip')}
                  </div>
                  <div className="col-4">
                    <label
                      htmlFor="pickup-city"
                      className="form-label text-muted small"
                    >
                      {t('addressModal.city')}
                      <span className="text-primary">*</span>
                    </label>
                    <input
                      type="text"
                      className="form-control"
                      name="city"
                      {...fieldProps('city')}
                      value={formData.city}
                      onChange={handleInputChange}
                    />
                    {fieldError('city')}
                  </div>
                  <div className="col-4">
                    <label className="form-label text-muted small">
                      {t('addressModal.county')}
                      <span className="text-primary">*</span>
                    </label>
                    <input
                      type="text"
                      className="form-control"
                      name="province"
                      value={formData.province}
                      onChange={handleInputChange}
                    />
                  </div>
                </div>

                {/* Phone number */}
                <div className="mb-3">
                  <div className="col-12">
                    <label
                      htmlFor="pickup-phone"
                      className="form-label text-muted small"
                    >
                      {t('addressModal.phone')}
                      <span className="text-primary">*</span>
                    </label>
                    <input
                      type="text"
                      className="form-control"
                      name="phone"
                      {...fieldProps('phone')}
                      value={formData.phone ? formData.phone : ''}
                      onChange={handleInputChange}
                    />
                    {fieldError('phone')}
                  </div>
                </div>
              </div>
            </div>

            {/* Modal Footer */}
            <div
              className="modal-footer border-0 pt-0 w-100 d-flex justify-content-center gap-2 pb-2"
              style={{ padding: 0 }}
            >
              <button
                type="button"
                className="btn btn-light btn-sm text-dark fw-medium border border-dark rounded"
                style={{ width: '45%' }}
                onClick={onClose}
              >
                {t('buttons.cancel')}
              </button>
              <button
                type="button"
                className="btn btn-primary btn-sm text-light fw-medium border border-dark rounded"
                style={{ width: '45%' }}
                onClick={() => handleSubmit()}
              >
                {t('buttons.saveAddress')}
              </button>
            </div>
          </div>
        </div>
      </div>
    </>,
    document.body
  );
};

export default AddressModal;
